import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { UserPlus, Loader2, ShieldCheck, Pencil, Trash2, KeyRound, Building2, Copy, Wifi } from "lucide-react";

const fmtWhen = (iso) => {
  if (!iso) return "mai";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "adesso";
  if (s < 3600) return `${Math.floor(s / 60)} min fa`;
  if (s < 86400) return `${Math.floor(s / 3600)} h fa`;
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
};

const ROLES = [
  { value: "admin", label: "Admin", desc: "accesso completo" },
  { value: "commercial_manager", label: "Responsabile Commerciale", desc: "vede tutta la rete (lettura)" },
  { value: "back_office", label: "Back Office", desc: "come Responsabile Commerciale" },
  { value: "head_of_partner", label: "Head of Partner", desc: "visibilità globale (lettura)" },
  { value: "technical", label: "Area Tecnica", desc: "Tools + Gestionale/Magazzino in lettura (no affidamenti)" },
  { value: "agency", label: "Agenzia", desc: "vede solo i propri partner" },
  { value: "partner", label: "Partner", desc: "vede solo la propria scheda" },
];
const ROLE_LABEL = Object.fromEntries(ROLES.map((r) => [r.value, r.label]));
const ROLE_STYLE = {
  admin: "bg-primary text-primary-foreground",
  commercial_manager: "bg-amber-500 text-black",
  back_office: "bg-orange-600 text-white",
  head_of_partner: "bg-blue-600 text-white",
  technical: "bg-teal-600 text-white",
  agency: "bg-emerald-600 text-white",
  partner: "bg-slate-500 text-white",
};
const EMPTY = { name: "", email: "", password: "", role: "agency", agency_id: "none", partner_id: "none", extra_pages: [] };

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [pwDialog, setPwDialog] = useState(null); // user object
  const [newPw, setNewPw] = useState("");
  const [actBusy, setActBusy] = useState(false);
  const [actPreview, setActPreview] = useState(null); // {count, agencies:[...]}
  const [actResult, setActResult] = useState(null); // {created, users:[{agency,email,password}]}
  const [actSelected, setActSelected] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    const [u, a, p] = await Promise.all([api.get("/users"), api.get("/agencies"), api.get("/partners")]);
    setUsers(u.data); setAgencies(a.data); setPartners(p.data);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const [presence, setPresence] = useState({ users: [], online_count: 0 });
  useEffect(() => {
    const loadPres = () => api.get("/presence").then((r) => setPresence(r.data)).catch(() => {});
    loadPres();
    const iv = setInterval(loadPres, 20000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (actPreview?.agencies) setActSelected(new Set(actPreview.agencies.map((a) => a.id)));
  }, [actPreview]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const openNew = () => { setEditing(null); setForm(EMPTY); setDialog(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name || "", email: u.email, password: "", role: u.role, agency_id: u.agency_id || "none", partner_id: u.partner_id || "none", extra_pages: u.extra_pages || [] });
    setDialog(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const agency_id = form.role === "agency" && form.agency_id !== "none" ? form.agency_id : null;
      const partner_id = form.role === "partner" && form.partner_id !== "none" ? form.partner_id : null;
      if (editing) {
        await api.put(`/users/${editing.id}`, { name: form.name, email: form.email, role: form.role, agency_id, partner_id, extra_pages: form.extra_pages || [] });
        toast.success("Utente aggiornato");
      } else {
        await api.post("/auth/register", { name: form.name, email: form.email, password: form.password, role: form.role, agency_id, partner_id, extra_pages: form.extra_pages || [] });
        toast.success("Utente creato");
      }
      setDialog(false); setForm(EMPTY); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const resetPw = async () => {
    try {
      await api.post(`/users/${pwDialog.id}/reset-password`, { password: newPw });
      toast.success("Password aggiornata");
      setPwDialog(null); setNewPw("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Eliminare l'utente "${u.name || u.email}"?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Utente eliminato"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const previewActivate = async () => {
    setActBusy(true);
    try {
      const r = await api.post("/users/activate-agencies", { confirm: false });
      if (!r.data.count) { toast.info("Tutte le agenzie hanno già un utente collegato."); return; }
      setActPreview(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setActBusy(false); }
  };

  const confirmActivate = async () => {
    setActBusy(true);
    try {
      const r = await api.post("/users/activate-agencies", { confirm: true, agency_ids: Array.from(actSelected) });
      setActPreview(null);
      setActResult(r.data);
      toast.success(`Creati ${r.data.created} nuovi utenti agenzia`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setActBusy(false); }
  };

  const copyCreds = () => {
    const txt = (actResult?.users || []).map((u) => `${u.agency}\t${u.email}\t${u.password}`).join("\n");
    navigator.clipboard.writeText(txt).then(() => toast.success("Credenziali copiate")).catch(() => {});
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Gestione Utenti</h1>
          <p className="text-sm text-muted-foreground">Crea e gestisci gli accessi alla piattaforma.</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Button onClick={openNew} data-testid="add-user-btn"><UserPlus className="mr-2 h-4 w-4" /> Nuovo Utente</Button>
          <Button variant="outline" onClick={previewActivate} disabled={actBusy} data-testid="activate-agencies-btn">
            {actBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Building2 className="mr-2 h-4 w-4" />} Attiva nuove agenzie
          </Button>
        </div>
      </div>

      <Card className="p-6">
        <div className="flex items-start gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground"><ShieldCheck className="h-5 w-5" /></div>
          <div className="space-y-2 text-sm text-muted-foreground">
            <p><strong className="text-foreground">Ruoli disponibili:</strong></p>
            <div className="flex flex-wrap gap-2">
              {ROLES.map((r) => <Badge key={r.value} className={`border-0 ${ROLE_STYLE[r.value]}`}>{r.label} — {r.desc}</Badge>)}
            </div>
            <p>Collega gli utenti <b>Agenzia</b> alla relativa agenzia e i <b>Partner</b> alla relativa scheda per applicare la visibilità corretta.</p>
          </div>
        </div>
      </Card>

      <Card className="p-6" data-testid="presence-card">
        <div className="mb-4 flex items-center gap-2">
          <Wifi className="h-4 w-4 text-emerald-500" />
          <h2 className="font-heading text-lg font-bold tracking-tight">Utenti online</h2>
          <Badge className="ml-1 border-0 bg-emerald-600 text-white" data-testid="online-count">{presence.online_count} online</Badge>
          <span className="ml-auto text-[11px] text-muted-foreground">aggiornamento ogni 20s</span>
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {(presence.users || []).map((u) => (
            <div key={u.id} className="flex items-center gap-3 rounded-md border border-border p-3" data-testid={`presence-row-${u.id}`}>
              <span className="relative flex h-2.5 w-2.5 shrink-0">
                {u.online && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />}
                <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${u.online ? "bg-emerald-500" : "bg-muted-foreground/40"}`} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-semibold">{u.name || u.email}{u.id === me?.id && <span className="ml-1 text-[10px] text-muted-foreground">(tu)</span>}</span>
                  <Badge className={`border-0 text-[10px] ${ROLE_STYLE[u.role] || ""}`}>{ROLE_LABEL[u.role] || u.role}</Badge>
                </div>
                <div className="truncate text-[11px] text-muted-foreground">{u.agency_name || u.email}</div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  {u.online ? <span className="font-semibold text-emerald-600">Attivo ora</span> : <>Visto {fmtWhen(u.last_seen)}</>}
                  <span className="mx-1">·</span>Accesso {fmtWhen(u.last_login)}
                </div>
              </div>
            </div>
          ))}
          {(!presence.users || presence.users.length === 0) && <p className="text-sm text-muted-foreground">Nessun dato di presenza ancora.</p>}
        </div>
      </Card>

      <Card className="p-0" data-testid="users-list-card">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Nome</TableHead><TableHead>Email</TableHead><TableHead>Ruolo</TableHead>
              <TableHead>Collegamento</TableHead><TableHead>Ultimo accesso</TableHead><TableHead className="text-right">Azioni</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {users.map((u) => {
                const stale = u.role === "agency" && (!u.last_login || Date.now() - new Date(u.last_login).getTime() > 7 * 86400000);
                return (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`} className={stale ? "bg-red-500/5" : ""}>
                  <TableCell className="font-semibold">{u.name || "—"}{u.id === me?.id && <span className="ml-1 text-[10px] text-muted-foreground">(tu)</span>}</TableCell>
                  <TableCell className="text-muted-foreground">{u.email}</TableCell>
                  <TableCell><Badge className={`border-0 ${ROLE_STYLE[u.role] || ""}`}>{ROLE_LABEL[u.role] || u.role}</Badge></TableCell>
                  <TableCell className="text-sm text-muted-foreground">{u.agency_name || u.partner_name || "—"}</TableCell>
                  <TableCell className="text-sm">
                    <span className={stale ? "font-semibold text-red-500" : "text-muted-foreground"} data-testid={`last-login-${u.id}`}>{fmtWhen(u.last_login)}</span>
                    {stale && <span className="ml-1 text-[10px] font-semibold text-red-500">· inattivo</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => { setPwDialog(u); setNewPw(""); }} data-testid={`reset-pw-${u.id}`}><KeyRound className="h-4 w-4" /></Button>
                      {u.id !== me?.id && <Button variant="ghost" size="icon" onClick={() => remove(u)} data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
              {users.length === 0 && <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground">Nessun utente.</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader><DialogTitle>{editing ? "Modifica Utente" : "Nuovo Utente"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label className="text-xs">Nome</Label><Input value={form.name} onChange={set("name")} data-testid="uf-name" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="uf-email" /></div>
            {!editing && <div className="space-y-1.5"><Label className="text-xs">Password</Label><Input type="password" value={form.password} onChange={set("password")} data-testid="uf-password" /></div>}
            <div className="space-y-1.5">
              <Label className="text-xs">Ruolo</Label>
              <Select value={form.role} onValueChange={set("role")}>
                <SelectTrigger data-testid="uf-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.role === "agency" && (
              <div className="space-y-1.5">
                <Label className="text-xs">Agenzia collegata</Label>
                <Select value={form.agency_id} onValueChange={set("agency_id")}>
                  <SelectTrigger data-testid="uf-agency"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Nessuna</SelectItem>{agencies.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {form.role === "partner" && (
              <div className="space-y-1.5">
                <Label className="text-xs">Partner collegato</Label>
                <Select value={form.partner_id} onValueChange={set("partner_id")}>
                  <SelectTrigger data-testid="uf-partner"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Nessuno</SelectItem>{partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-xs">Pagine extra abilitate</Label>
              <p className="text-[11px] text-muted-foreground">Accesso in sola visualizzazione (con costi e margini, senza modifica/import) indipendentemente dal ruolo.</p>
              {[["listino", "Listino Prodotti"], ["preventivatore", "Preventivatore"], ["backoffice", "Dashboard BackOffice"], ["monitoring", "Torri Monitoring"]].map(([key, label]) => (
                <label key={key} className="flex cursor-pointer items-center gap-2.5 rounded-md border border-border px-3 py-2 text-sm" data-testid={`uf-page-${key}`}>
                  <Checkbox checked={(form.extra_pages || []).includes(key)}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, extra_pages: v ? [...new Set([...(f.extra_pages || []), key])] : (f.extra_pages || []).filter((x) => x !== key) }))}
                    data-testid={`uf-page-cb-${key}`} />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving || !form.name || (!editing && (!form.email || !form.password))} data-testid="save-user-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? "Salva" : "Crea Utente")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwDialog} onOpenChange={(o) => !o && setPwDialog(null)}>
        <DialogContent data-testid="reset-pw-dialog">
          <DialogHeader><DialogTitle>Reset password · {pwDialog?.name || pwDialog?.email}</DialogTitle></DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs">Nuova password (min 6 caratteri)</Label>
            <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="new-pw-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwDialog(null)}>Annulla</Button>
            <Button onClick={resetPw} disabled={newPw.length < 6} data-testid="save-pw-btn">Aggiorna password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!actPreview} onOpenChange={(o) => !o && setActPreview(null)}>
        <DialogContent data-testid="activate-agencies-dialog">
          <DialogHeader><DialogTitle>Attiva nuove agenzie</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p>Sono state trovate <b className="text-primary">{actPreview?.count}</b> agenzie senza utente collegato. Seleziona quali attivare — verrà creato un accesso <b>Agenzia</b> con password temporanea.</p>
            <div className="flex items-center justify-between">
              <button type="button" className="text-xs font-semibold text-primary hover:underline" data-testid="act-select-all"
                onClick={() => setActSelected((s) => s.size === (actPreview?.agencies || []).length ? new Set() : new Set((actPreview?.agencies || []).map((a) => a.id)))}>
                {actSelected.size === (actPreview?.agencies || []).length && actSelected.size > 0 ? "Deseleziona tutte" : "Seleziona tutte"}
              </button>
              <span className="text-xs text-muted-foreground" data-testid="act-selected-count">{actSelected.size} selezionate</span>
            </div>
            <div className="max-h-56 overflow-y-auto rounded-lg border border-white/10 bg-black/30 p-1">
              {(actPreview?.agencies || []).map((a) => {
                const on = actSelected.has(a.id);
                return (
                  <label key={a.id} className="flex cursor-pointer items-center gap-2 rounded-md border-b border-white/5 px-2 py-1.5 text-xs last:border-0 hover:bg-white/5" data-testid={`act-agency-${a.id}`}>
                    <input type="checkbox" checked={on} onChange={() => setActSelected((s) => { const n = new Set(s); if (n.has(a.id)) n.delete(a.id); else n.add(a.id); return n; })} data-testid={`act-check-${a.id}`} className="h-4 w-4 accent-primary" />
                    <span className="min-w-0 flex-1 truncate">{a.name}</span>
                    <span className="shrink-0 text-muted-foreground">{a.email || "email generata automaticamente"}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActPreview(null)}>Annulla</Button>
            <Button onClick={confirmActivate} disabled={actBusy || actSelected.size === 0} data-testid="confirm-activate-btn">
              {actBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Crea ${actSelected.size} utenti`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!actResult} onOpenChange={(o) => !o && setActResult(null)}>
        <DialogContent data-testid="activate-result-dialog">
          <DialogHeader><DialogTitle>Credenziali generate ({actResult?.created})</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Salva o copia queste credenziali: le password non saranno più visibili. Potrai comunque reimpostarle dalla lista utenti.</p>
            <div className="max-h-72 overflow-y-auto rounded-lg border border-white/10">
              <Table>
                <TableHeader><TableRow><TableHead>Agenzia</TableHead><TableHead>Email</TableHead><TableHead>Password</TableHead></TableRow></TableHeader>
                <TableBody>
                  {(actResult?.users || []).map((u, i) => (
                    <TableRow key={i} data-testid={`act-cred-${i}`}>
                      <TableCell className="text-xs font-semibold">{u.agency}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{u.email}</TableCell>
                      <TableCell className="font-mono text-xs">{u.password}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={copyCreds} data-testid="copy-creds-btn"><Copy className="mr-2 h-4 w-4" /> Copia tutto</Button>
            <Button onClick={() => setActResult(null)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
